import {ExpertSystemEquipmentType} from '@domain/expert-system'
import {Select} from '@hconnect/common/components'
import {Autocomplete} from '@hconnect/common/components/formFields'
import {InstallationStatus, KpiOption, MillType} from '@hconnect/common/types'
import {Add, DeleteOutlineOutlined, ExpandMore} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import {EventStoppedMainEquipmentSearch} from '@widgets/common-shift-event'
import {useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, PROVIDERS} from '../../consts'
import {useExpandedPanels} from '../../hooks'
import {Item} from '../../types'
import {FieldErrors} from '../../validation'
import {InstallationStatusField} from '../InstallationStatusField'

const YEAR_OPTIONS = Array.from({length: 16}, (_, i) => {
  const year = String(2020 + i)
  return {value: year, label: year}
})

type KpiSectionProps = {
  items: Item[]
  kpiOptions: KpiOption[]
  setItems: React.Dispatch<React.SetStateAction<Item[]>>
  equipment: ExpertSystemEquipmentType
  errors: FieldErrors
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>
  plantId: string
}

export const KpiSection: React.FC<KpiSectionProps> = ({
  kpiOptions,
  items,
  setItems,
  equipment,
  errors,
  setIsDirty,
  plantId
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {expandedPanels, togglePanel, expandPanel, removePanel} = useExpandedPanels(items.length)

  const newItemRef = useRef<HTMLDivElement | null>(null)
  const shouldScrollRef = useRef(false)

  useEffect(() => {
    if (shouldScrollRef.current && newItemRef.current) {
      newItemRef.current.scrollIntoView({behavior: 'smooth', block: 'nearest'})
      shouldScrollRef.current = false
    }
  }, [items.length])

  const handleChange = (key: keyof Item, idx: number, value?: string) => {
    setItems((prev) => {
      return prev.map((item, index) => (index === idx ? {...item, [key]: value} : item))
    })
    setIsDirty(true)
  }

  const emptyOption = useMemo(
    () => ({
      value: '',
      label: t('label.emptyOption')
    }),
    [t]
  )

  const providersOptions = useMemo(() => {
    const options = Object.keys(PROVIDERS).map((key) => ({
      label: PROVIDERS[key],
      value: key
    }))
    return [emptyOption, ...options]
  }, [emptyOption])

  const millTypeOptions = useMemo(() => {
    if (equipment === ExpertSystemEquipmentType.Kiln) return []
    return [
      emptyOption,
      ...Object.entries(MillType).map(([, key]) => ({
        value: key,
        label: t(`responsiblePersonDialog.millTypes.${key}`)
      }))
    ]
  }, [emptyOption, equipment, t])

  const yearOptions = useMemo(() => [emptyOption, ...YEAR_OPTIONS], [emptyOption])

  const handleInstallationStatusChange = (
    index: number,
    installationStatus: InstallationStatus | undefined,
    installationStatusText: string | undefined
  ) => {
    setItems((prev) =>
      prev.map((item, idx) =>
        idx === index ? {...item, installationStatus, installationStatusText} : item
      )
    )
    setIsDirty(true)
  }

  const handleAddClick = () => {
    setItems((prev) => {
      const newItems = [...prev, {name: '', kpiAssignmentId: ''}]
      expandPanel(newItems.length - 1)
      return newItems
    })
    shouldScrollRef.current = true
    setIsDirty(true)
  }

  const handleKpiChange = (index: number, value: KpiOption | KpiOption[] | null) => {
    if (!value || Array.isArray(value)) return
    setItems((prev) => {
      return prev.map((item, idx) =>
        idx === index ? {...item, kpiAssignmentId: value.id, label: value.label} : item
      )
    })
    setIsDirty(true)
  }

  const handleDeleteClick = (index: number) => {
    setItems((prev) => {
      return prev.filter((_, idx) => idx !== index)
    })
    removePanel(index)
    setIsDirty(true)
  }

  const getValue = (item: Item): KpiOption | null => {
    return item.kpiAssignmentId === '' || !item.label
      ? null
      : {id: item.kpiAssignmentId, label: item.label}
  }

  const isMill = equipment === ExpertSystemEquipmentType.Mill

  return (
    <Box data-test-id="item-kpis" pt={2}>
      <Grid display="flex" marginBottom={1} justifyContent="space-between" alignItems="center">
        <Typography variant="h4">{t('responsiblePersonDialog.label.items')}</Typography>
        <Button
          onClick={handleAddClick}
          data-test-id="add-kpi-item-button"
          variant="text"
          startIcon={<Add />}
        >
          {t('responsiblePersonDialog.action.add')}
        </Button>
      </Grid>

      {!items.length && (
        <Typography variant="subtitle1" color="GrayText">
          {t('responsiblePersonDialog.label.noItemsForPlant')}
        </Typography>
      )}

      <Grid display="flex" flexDirection="column" gap={1}>
        {items.map((item, index) => (
          <Accordion
            key={item.id || index}
            ref={index === items.length - 1 ? newItemRef : undefined}
            expanded={expandedPanels.has(index)}
            onChange={() => togglePanel(index)}
            disableGutters
            data-test-id={`kpi-item-accordion-${index}`}
            sx={{
              '&&:hover .MuiAccordionSummary-root': {
                backgroundColor: 'transparent'
              },
              '&&.Mui-expanded': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                px: 0,
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  mr: 1.5
                }
              }}
            >
              <Typography>
                {item.name || `<${t('responsiblePersonDialog.label.newItem')}>`}
              </Typography>
              <IconButton
                data-test-id={`delete-kpi-item-${index}`}
                onClick={(e) => {
                  e.stopPropagation()
                  handleDeleteClick(index)
                }}
              >
                <DeleteOutlineOutlined color="error" />
              </IconButton>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: 0,
                '&&': {
                  backgroundColor: 'transparent'
                },
                '&&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              <Grid container columnSpacing={2} alignItems="center">
                <Grid item xs={6} mb={2}>
                  <TextField
                    fullWidth
                    variant="filled"
                    data-test-id={`kpi-item-name-${index}`}
                    value={item.name}
                    error={!!errors.kpiNodes?.[item.id ?? `temp-${index}`]}
                    helperText={
                      errors.kpiNodes?.[item.id ?? `temp-${index}`]
                        ? t('responsiblePersonDialog.error.kpiNameRequired')
                        : ''
                    }
                    onChange={(e) => handleChange('name', index, e.target.value)}
                    label={t('responsiblePersonDialog.label.name')}
                  />
                </Grid>
                <Grid item xs={6} mb={2}>
                  <Autocomplete
                    value={getValue(item)}
                    name={`kpi-dropdown-${index}`}
                    options={kpiOptions}
                    fullWidth
                    data-test-id={`item-kpi-dropdown-${index}`}
                    disabled={kpiOptions.length === 0}
                    onChange={(_, option) => handleKpiChange(index, option)}
                    label={t('responsiblePersonDialog.label.kpi')}
                  />
                </Grid>

                <Grid item xs={6} mb={2}>
                  <EventStoppedMainEquipmentSearch
                    plantId={plantId}
                    onChange={(equipment) => handleChange('upmDepartmentId', index, equipment)}
                    value={item.upmDepartmentId}
                    label={t('responsiblePersonDialog.label.equipmentNameInJanus')}
                  />
                </Grid>

                <Grid item xs={isMill ? 3 : 6} mb={2}>
                  <Select
                    value={item.provider}
                    name={`provider-dropdown-${index}`}
                    data-test-id={`item-provider-dropdown-${index}`}
                    onChange={(value) => handleChange('provider', index, value)}
                    label={t('responsiblePersonDialog.label.provider')}
                    options={providersOptions}
                  />
                </Grid>

                {isMill && (
                  <>
                    <Grid item xs={3} mb={2}>
                      <Select
                        value={item.millType}
                        onChange={(value) => handleChange('millType', index, value)}
                        label={t('label.type')}
                        options={millTypeOptions}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        value={item.expertSystemName}
                        onChange={(e) => handleChange('expertSystemName', index, e.target.value)}
                        variant="filled"
                        fullWidth
                        label={t('label.expertSystemName')}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={isMill ? 3 : 6}>
                  <InstallationStatusField
                    item={item}
                    index={index}
                    onChange={handleInstallationStatusChange}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Select
                    value={item.installationYear}
                    name={`installation-year-dropdown-${index}`}
                    data-test-id={`item-installation-year-dropdown-${index}`}
                    onChange={(value) => handleChange('installationYear', index, value)}
                    label={t('responsiblePersonDialog.label.installedPlannedYear')}
                    options={yearOptions}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid>
    </Box>
  )
}
